import { Elements } from "@stripe/react-stripe-js";
import { useDevice } from "get-life-storybook-ts";
import { useContext, useEffect, useState } from "react";
import PaymentPageController from "./PaymentPage.controller";
import PaymentStepper from "./v3/components/PaymentStepper.component";
import OrderSummaryV3 from "./v3/components/OrderSummaryV3.component";
import MobileSummary from "./v3/components/MobileSummary.component";
import PersonalDataSection from "./v3/components/PersonalDataSection.component";
import CardFormSection from "./v3/components/CardFormSection.component";
import HelpBlock from "./v3/components/HelpBlock.component";
import Testimonial from "./v3/components/Testimonial.component";
import TrustGrid from "./v3/components/TrustGrid.component";
import StickyMobileCTA from "./v3/components/StickyMobileCTA.component";
import { WalletType } from "./v3/components/PaymentMethodPicker.component";
import { StripeContext, StripeProvider } from "../../../../context/StripeContext.context";
import {
  GetlifeContext,
  LanguageT,
} from "../../../../context/GetLifeContext.context";
import { GlobalYearlyPayments } from "../../../../utils/InternationlChanges";
import ConditionsModalAB from "../../../../components/ConditionsModal/ConditionsModalAB";
import { Promotion } from "../../../../api/request/Lead/Interfaces/ProductInterface.response";
import { PurchasePeriodT } from "../../../../api/request/Lead/Interfaces/DataInterface.response";
import { DOMAIN } from "../../../../constants/Global";
// TODO: validar copy con legal — D.lgs. 209/2025 / EU 2023/2673 (deadline 19/06/2026)
import {
  ITComplianceOnly,
  IntermediaryBadge,
  PrecontractualDocs,
  WithdrawalRightNotice,
} from "../../../../components/Compliance";
import { isMockPayment } from "../PaymentPage/__fixtures__/paymentMockData";
import "./PaymentPage.scss";
import "./v3/PaymentPageV3.styles.scss";

const PaymentPageChallenger = () => {
  const {
    handleCheckbox,
    handleCheckbox2,
    handleIsLoading,
    handleStripeStatus,
    isButtonDisabled,
    isUserDataAvailable,
    leadId,
    navigateTo,
    payConfirm,
    payConfirm2,
    saveData,
    stripePromise,
    stripeInputStatus,
    token,
    translate,
    checkout,
    nodeConditionsModal,
    handleConditions,
    couponInfo,
    validateCoupon,
    handlePurchasePeriod,
    handleRemoveCoupon,
    setupIntent,
    handleAddCoupon,
    capitals,
    prices,
    promotion,
  } = PaymentPageController();

  const { brokerPhone } = useContext(GetlifeContext);
  const { isMobile } = useDevice();

  const [selectedMethod, setSelectedMethod] = useState<WalletType>("card");
  const [editingPersonalData, setEditingPersonalData] = useState(false);

  useEffect(() => {
    if (isUserDataAvailable) setEditingPersonalData(false);
  }, [isUserDataAvailable]);

  if (!checkout) return null;

  const phone = process.env.REACT_APP_TEL || brokerPhone || undefined;
  const dataDone = isUserDataAvailable && !editingPersonalData;

  const summaryProps = {
    checkout,
    capitals,
    prices,
    promotion,
    isAvailableToggle: GlobalYearlyPayments[DOMAIN as LanguageT],
    translate,
    validateCoupon,
    couponInfo,
    handlePurchasePeriod,
    handleAddCoupon,
    handleRemoveCoupon,
    onConditionsClick: handleConditions,
  };

  const mockMode = isMockPayment();

  const formColumn = (
    <>
      {isMobile && (
        <ITComplianceOnly>
          <IntermediaryBadge translate={translate} compact />
        </ITComplianceOnly>
      )}

      <PersonalDataSection
        userData={checkout}
        saveData={(data: any) => {
          saveData(data);
          setEditingPersonalData(false);
        }}
        done={dataDone}
        onEdit={() => setEditingPersonalData(true)}
        translate={translate}
      />

      {mockMode ? (
        <div className="paymentV3__mockCardPlaceholder p-6 border border-dashed border-blue-gray-300 rounded-lg bg-blue-gray-50 text-blue-gray-700 my-4 max-w-[960px] mx-auto">
          <strong>Mock preview</strong> — Sezione 03 (Stripe card form, checkboxes T&amp;C, Pulsante Paga) non renderizzata in modalità mock per evitare la chiamata a Stripe. In produzione i bloques compliance precederebbero il modulo di pagamento.
        </div>
      ) : (
        <CardFormSection
          enabled={dataDone}
          setupIntent={setupIntent}
          checkout={checkout}
          handleStripeStatus={handleStripeStatus}
          stripeInputStatus={stripeInputStatus}
          payConfirm={payConfirm}
          payConfirm2={payConfirm2}
          handleCheckbox={handleCheckbox}
          handleCheckbox2={handleCheckbox2}
          isButtonDisabled={isButtonDisabled()}
          selectedMethod={selectedMethod}
          onMethodChange={setSelectedMethod}
          translate={translate}
        />
      )}

      <div className="paymentV3FormCol__back">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.history.back();
          }}
          className="paymentV3FormCol__backLink"
        >
          ← {translate("paymentV3.section.back")}
        </a>
      </div>
    </>
  );

  const layout = (
    <>
      {isMobile && (
        <div className="paymentV3__mobileSummaryWrap">
          <MobileSummary {...summaryProps} />
        </div>
      )}

      <div className="paymentV3__split">
        <div className="paymentV3__formCol">{formColumn}</div>

        {!isMobile && (
          <div className="paymentV3__sidebar">
            <OrderSummaryV3 {...summaryProps} />
            <Testimonial translate={translate} variant="sidebar" />
            <ITComplianceOnly>
              <IntermediaryBadge translate={translate} />
              <PrecontractualDocs translate={translate} />
              <WithdrawalRightNotice translate={translate} />
            </ITComplianceOnly>
            <TrustGrid translate={translate} variant="sidebar" />
            <HelpBlock phone={phone} translate={translate} />
          </div>
        )}
      </div>

      {isMobile && (
        <div className="paymentV3__mobileExtras">
          <Testimonial translate={translate} variant="mobile" />
          <ITComplianceOnly>
            <IntermediaryBadge translate={translate} />
            <PrecontractualDocs translate={translate} />
            <WithdrawalRightNotice translate={translate} />
          </ITComplianceOnly>
          <TrustGrid translate={translate} variant="mobile" />
          <HelpBlock phone={phone} translate={translate} />
        </div>
      )}

      {isMobile && !mockMode && (
        <StickyMobileCTA
          checkout={checkout}
          prices={prices}
          isButtonDisabled={isButtonDisabled()}
          selectedMethod={selectedMethod}
          translate={translate}
        />
      )}
    </>
  );

  return (
    <>
      <div className="paymentV3">
        <PaymentStepper active={1} translate={translate} />

        <section className="paymentV3__hero">
          <div className="paymentV3__heroEyebrow">
            {translate("paymentV3.hero.eyebrow")}
          </div>
          <h1 className="paymentV3__heroTitle">
            {translate("paymentV3.hero.title")}{" "}
            <em>{translate("paymentV3.hero.titleAccent")}</em>
          </h1>
          <p className="paymentV3__heroSubtitle">
            {translate("paymentV3.hero.subtitle")}
          </p>
        </section>

        {mockMode ? (
          <StripeContext.Provider value={{ handleSubmit: () => {} } as any}>
            {layout}
          </StripeContext.Provider>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: setupIntent.clientSecret }}
          >
            <StripeProvider
              handleIsLoading={handleIsLoading}
              setupIntent={setupIntent}
              leadId={leadId}
              token={token}
              checkout={checkout}
              navigate={navigateTo}
            >
              {layout}
            </StripeProvider>
          </Elements>
        )}

      </div>

      {checkout?.promotionalCode && (
        <ConditionsModalAB
          couponInfo={couponInfo}
          nodeFakeDoorModal={nodeConditionsModal}
          promotion={
            {
              conditions: couponInfo?.conditions,
              promotionalCode: couponInfo?.code,
            } as Promotion
          }
          collection={checkout.feeCollection as any}
          period={
            (checkout.period?.toLowerCase() || "monthly") as PurchasePeriodT
          }
          handleSubmit={() => nodeConditionsModal.current?.handleShowPortal()}
        />
      )}
    </>
  );
};

export default PaymentPageChallenger;
